import Cookies from "js-cookie";
import fireEREvt from "../util/eventRecorderClient";
import { Storage } from "../util/Storage";
import { RegUIClient, RegUserInfo } from "../type";

const USER_INFO_EVENT = "reg-user-info-ready";
const REG_USER_SIGNED_IN_EVENT = "reg-user-signed-in";
const SESSION_ID_COOKIE = "session_id";
const USER_INFO_TIMEOUT = 500;
const SUBS_STATUS_LOCAL_STORAGE_KEY = "bbads_subs_status";

const local = Storage.createLocalStorage();

export const SUBSCRIBER_TYPE = Object.freeze({
    BBG_SUBSCRIBER: "yes",
    TERMINAL_SUBSCRIBER: "term",
    NON_SUBSCRIBER: "no",
    /**
     * Sent when the reg-ui-client has not reported yet and no previous page
     * load worked it out. Deliberately not "no": guessing a subscriber is a
     * non-subscriber is the mistake this exists to avoid, and an explicit
     * value is targetable and reportable in a way that omitting the key
     * would not be. AdOps must allow it on the `subscriber` key.
     */
    UNKNOWN: "unknown"
});

export type SubscriberChangeListener = (subsInfo: string) => void;

/**
 * This implementation tentatively relies on global events emitted by reg-ui-client library
 * @class UserInfo
 */
export default class UserInfo {

    _userInfo: RegUserInfo | null = null;
    _setUserInfoWithResolve: undefined | (({ detail }: { detail: RegUserInfo }) => void);

    /**
     * Subscriber type currently in effect, i.e. the one the listeners were last
     * told about. Also what we compare against to avoid redundant writes.
     */
    _publishedSubsInfo: string | undefined = undefined;
    _subscriberListeners: SubscriberChangeListener[] = [];
    /**
     * Whether reg-ui-client has finished its own user-info fetch. Latched by
     * the events below, because the global it sets is not a reliable signal
     * for a logged-out user.
     */
    _regDataReady = false;
    _regDataHandler: undefined | (() => void);

    getSessionId(timeoutInMs?: number) {
        const sessionId = this.getSessionIdSync();

        if (sessionId) {
            return Promise.resolve(sessionId);
        }

        return this.getUserInfo(timeoutInMs).then((userInfo) => userInfo.sessionId);
    }

    /**
     * Subscriber type according to @client, or undefined if it could not be
     * derived - undefined rather than NON_SUBSCRIBER so a throw is not
     * mistaken for an answer and cached as one.
     */
    _calculateSubsInfo(client?: RegUIClient): string | undefined {
        try {
            // isTerminalSubscription check need to be done before BBG
            // terminal subscription is one of BBG subs.
            if (client?.isTerminalSubscriber?.()) {
                return SUBSCRIBER_TYPE.TERMINAL_SUBSCRIBER;
            }

            if (client?.isBBGSubscriber?.()) {
                return SUBSCRIBER_TYPE.BBG_SUBSCRIBER;
            }
        } catch (err) {
            // regUIClient is third-party - don't let a throw here take down
            // Sparkle._init (and every ad on the page) with it.
            fireEREvt("ad-err", `regUIClient threw while resolving subscriber type: ${ err }`);

            return undefined;
        }

        return SUBSCRIBER_TYPE.NON_SUBSCRIBER;
    }

    /**
     * Last subscriber status persisted by an earlier page load, or undefined
     * on a miss - note that a storage miss surfaces as an empty string rather
     * than undefined, so that is treated as a miss too.
     */
    _readSubsCache(): string | undefined {
        const cached = local.getObj(SUBS_STATUS_LOCAL_STORAGE_KEY) as unknown;

        return typeof cached === "string" && cached !== "" ? cached : undefined;
    }

    /**
     * Derives the subscriber type from the reg-ui-client and, if it is a real
     * answer, publishes and stores it.
     *
     * Gated on the client having finished its own fetch:
     * isBBGSubscriber()/isTerminalSubscriber() answer false until it has, and
     * regUIClient is assigned before that, so reading it early reports a real
     * subscriber as a non-subscriber.
     *
     * @returns the derived subscriber type, or undefined if there was no real
     * answer to be had
     */
    _setSubsInfoFromClient(): string | undefined {
        const client = this._isRegDataReady() && window.__bloomberg__?.regUIClient;
        const subsInfo = client ? this._calculateSubsInfo(client) : undefined;

        if (subsInfo === undefined) {
            return undefined;
        }

        this._publishSubsInfo(subsInfo);
        // Every value we get this far with came from a ready client, so there
        // is no such thing as one not worth keeping for the next page load.
        local.setObj(SUBS_STATUS_LOCAL_STORAGE_KEY, subsInfo);

        return subsInfo;
    }

    _isRegDataReady(): boolean {
        return this._regDataReady || Boolean(window._regUserInfo);
    }

    /**
     * Watches for the reg-ui-client's data becoming trustworthy, and revises
     * the subscriber type when it does. Attached permanently and for the two
     * events that both mean "the client can now be believed":
     *
     * - reg-user-info-ready: its fetch landed, whatever the answer. The only
     *   signal a logged-out user produces.
     * - reg-user-signed-in: an in-page login, or - with source
     *   "already-signed-in" - the same landing for a user who was already
     *   authenticated when the page loaded.
     *
     * Logout is intentionally not handled: understating a subscriber briefly
     * is low-risk; overstating one is the risk this ticket exists to close.
     */
    _listenForRegData() {
        if (this._regDataHandler) {
            return;
        }

        this._regDataHandler = () => {
            this._regDataReady = true;
            this._setSubsInfoFromClient();
        };

        window.addEventListener(USER_INFO_EVENT, this._regDataHandler);
        window.addEventListener(REG_USER_SIGNED_IN_EVENT, this._regDataHandler);
    }

    /**
     * Records the subscriber type in effect and notifies the listeners. This
     * runs once per ad and per refresh, so it is a no-op unless the value
     * actually changed.
     *
     * @returns whether the value changed
     */
    _publishSubsInfo(subsInfo: string): boolean {
        if (this._publishedSubsInfo === subsInfo) {
            return false;
        }

        this._publishedSubsInfo = subsInfo;
        this._subscriberListeners.forEach((cb) => {
            try {
                cb(subsInfo);
            } catch (err) {
                // don't let one listener's failure stop the others or escape
                // into Sparkle._init.
                fireEREvt("ad-err", `subscriber change listener threw: ${ err }`);
            }
        });

        return true;
    }

    /**
     * Notifies when the subscriber type changes. The listener is called
     * immediately with the current value if one has already been resolved.
     */
    onSubscriberChange(cb: SubscriberChangeListener) {
        this._subscriberListeners.push(cb);

        if (this._publishedSubsInfo !== undefined) {
            cb(this._publishedSubsInfo);
        }
    }

    /**
     * Decides the subscriber type for this page and publishes it. Synchronous
     * and never awaited: it reports the best answer available right now and
     * revises it in place, through the listeners, if a better one turns up -
     * no ad ever waits on the reg-ui-client.
     *
     * Order of preference, and the whole of it:
     *
     * 1. the reg-ui-client, if its data has landed
     * 2. what the previous page load worked out and stored
     * 3. "unknown" - never a guessed "no"
     */
    resolveSubscriberInfo(): string {
        // Unconditionally, and before the guard below: being subscribed to
        // corrections is independent of having a value, and skipping it
        // because one is already published would freeze that value for the
        // life of the page. Idempotent, so repeat calls are free.
        this._listenForRegData();

        if (this._publishedSubsInfo !== undefined) {
            return this._publishedSubsInfo;
        }

        const derived = this._setSubsInfoFromClient();

        if (derived) {
            return derived;
        }

        const cached = this._readSubsCache();

        if (!cached) {
            fireEREvt("ad-RegUIClientNotReady");
        }

        const subsInfo = cached ?? SUBSCRIBER_TYPE.UNKNOWN;

        this._publishSubsInfo(subsInfo);

        return subsInfo;
    }

    /**
     * Subscriber type currently in effect, or undefined before
     * resolveSubscriberInfo has run. Pure read - no side effects, so it is
     * free to call per ad.
     */
    getSubscriberInfo(): string | undefined {
        return this._publishedSubsInfo;
    }

    /**
     * @deprecated use resolveSubscriberInfo / getSubscriberInfo instead
     */
    getCachedSubscriberInfo(): Promise<string> {
        return Promise.resolve(this.resolveSubscriberInfo());
    }

    getSessionIdSync() {
        return this._userInfo &&
            this._userInfo.sessionId || Cookies.get(SESSION_ID_COOKIE);
    }

    /**
     * @returns {Promise<Object>}
     */
    getUserInfo(timeoutInMs = USER_INFO_TIMEOUT): Promise<RegUserInfo> {
        this._userInfo = window._regUserInfo;

        if (this._userInfo) {
            return Promise.resolve(this._userInfo);
        }

        return new Promise((resolve, reject) => {
            this._setUserInfoWithResolve = this._setUserInfo.bind(this, resolve);
            // @ts-expect-error custom event
            window.addEventListener(USER_INFO_EVENT, this._setUserInfoWithResolve);

            setTimeout(() => {
                reject();
            }, timeoutInMs);
        });
    }

    /**
     * Sets @_userInfo and settles a promise
     * @param {Function} resolve
     * @param {Object|Boolean} detail
     * @private
     */
    _setUserInfo(
        resolve: (info: RegUserInfo | PromiseLike<RegUserInfo>) => void,
        { detail }: { detail: RegUserInfo }
    ) {
        this._userInfo = detail;
        resolve(this._userInfo);
    }
    /**
     * Cleans up references
     */
    destroy() {
        this._userInfo = null;
        // @ts-expect-error custom event
        window.removeEventListener(USER_INFO_EVENT, this._setUserInfoWithResolve);
        this._setUserInfoWithResolve = undefined;

        if (this._regDataHandler) {
            window.removeEventListener(USER_INFO_EVENT, this._regDataHandler);
            window.removeEventListener(REG_USER_SIGNED_IN_EVENT, this._regDataHandler);
            this._regDataHandler = undefined;
        }

        this._subscriberListeners = [];
        // reset the latches too, so a reused instance starts clean
        this._regDataReady = false;
        this._publishedSubsInfo = undefined;
    }

}
